import { Actor, Canvas, Color, Random, vec } from 'excalibur';
import { getDoodleColors, getTheme } from '../themes/theme';
import { DESIGN_HEIGHT, DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { css, DoodleKind, drawDoodle, drawPaperTexture, drawVignette } from '../utils/sketch';

const paperCache = new Map<string, Canvas>();

function getPaperGraphic(): Canvas {
  const theme = getTheme();
  const cached = paperCache.get(theme.id);
  if (cached) {
    return cached;
  }
  const graphic = new Canvas({
    width: DESIGN_WIDTH,
    height: DESIGN_HEIGHT,
    cache: true,
    draw: (ctx) => paintPaper(ctx),
  });
  paperCache.set(theme.id, graphic);
  return graphic;
}

export class PaperBackground extends Actor {
  constructor() {
    super({
      name: 'paper-background',
      pos: vec(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2),
      z: -100,
      graphic: getPaperGraphic(),
    });
  }

  refreshTheme(): void {
    this.graphics.use(getPaperGraphic());
  }
}

function paintPaper(ctx: CanvasRenderingContext2D): void {
  const theme = getTheme();
  const palette = theme.palette;
  const rng = new Random(SEEDS.paper);
  ctx.fillStyle = css(palette.paper);
  ctx.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);

  const washPositions: Array<{ x: number; y: number; radius: number }> = [
    { x: 96, y: 170, radius: 300 },
    { x: 640, y: 430, radius: 320 },
    { x: 70, y: 1010, radius: 340 },
    { x: 660, y: 1180, radius: 280 },
  ];
  const washes: Array<{ color: Color; x: number; y: number; radius: number }> = washPositions.map(
    (position, index) => ({
      color: theme.paper.washes[index % theme.paper.washes.length],
      x: position.x,
      y: position.y,
      radius: position.radius,
    }),
  );
  for (const wash of washes) {
    const gradient = ctx.createRadialGradient(wash.x, wash.y, 0, wash.x, wash.y, wash.radius);
    gradient.addColorStop(0, css(wash.color, 0.38));
    gradient.addColorStop(1, css(wash.color, 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(wash.x - wash.radius, wash.y - wash.radius, wash.radius * 2, wash.radius * 2);
  }

  drawPaperTexture(ctx, DESIGN_WIDTH, DESIGN_HEIGHT, rng, theme.paper.speckle, 2100);
  drawVignette(ctx, DESIGN_WIDTH, DESIGN_HEIGHT, theme.paper.vignette, 0.26);

  const corners: Array<{ kind: DoodleKind; x: number; y: number; size: number }> = [
    { kind: 'star', x: 84, y: 246, size: 22 },
    { kind: 'squiggle', x: 632, y: 348, size: 26 },
    { kind: 'cloud', x: 92, y: 1180, size: 24 },
    { kind: 'spiral', x: 634, y: 1120, size: 20 },
    { kind: 'heart', x: 620, y: 700, size: 17 },
    { kind: 'squiggle', x: 78, y: 620, size: 22 },
  ];
  const doodleColors = getDoodleColors();
  ctx.save();
  ctx.globalAlpha = 0.28;
  corners.forEach((corner, index) => {
    const color = index % 3 === 0 ? theme.paper.cornerInk : doodleColors[index % doodleColors.length];
    drawDoodle(ctx, corner.kind, corner.x, corner.y, corner.size, rng, color, 2.6);
  });
  ctx.restore();
}
